import { decode, encode } from "@googlemaps/polyline-codec";
import type { LngLatCoord } from "@gpx-spa/geo-domain";

/** Google Routes / Maps encoded polyline uses precision **5** (see Encoded Polyline Algorithm Format). */
const ENCODED_POLYLINE_PRECISION = 5;

function nowMs(): number {
  return typeof performance !== "undefined" &&
    typeof performance.now === "function"
    ? performance.now()
    : Date.now();
}

function decodeToLngLatCoords(encoded: string): LngLatCoord[] {
  const latLng = decode(encoded, ENCODED_POLYLINE_PRECISION) as [
    number,
    number,
  ][];
  return latLng.map(([lat, lng]) => [lng, lat] as LngLatCoord);
}

/**
 * Encodes domain **`[lng, lat][]`** to the same Google Encoded Polyline string format as Routes API **`encodedPolyline`**.
 */
export function encodeLngLatPathToGoogleEncodedPolyline(
  coords: LngLatCoord[],
): string {
  if (coords.length < 2) {
    throw new Error("Encoded polyline requires at least two vertices.");
  }
  const path = coords.map(([lng, lat]) => [lat, lng] as [number, number]);
  return encode(path, ENCODED_POLYLINE_PRECISION);
}

/**
 * Decodes Google Routes API **`polyline.encodedPolyline`** into WGS84 **`[lng, lat][]`** for domain types.
 * Uses **`@googlemaps/polyline-codec`** (Google’s JS implementation of the same algorithm as in the official docs).
 */
export function decodeGoogleRoutesEncodedPolyline(
  encoded: string,
): LngLatCoord[] {
  return decodeToLngLatCoords(encoded);
}

/** Same decode plus wall-clock duration and vertex count (for routing adapter meta / benchmarks). */
export function decodeGoogleRoutesEncodedPolylineMeasured(encoded: string): {
  coordinates: LngLatCoord[];
  decodeMs: number;
  vertexCount: number;
} {
  const t0 = nowMs();
  const coordinates = decodeToLngLatCoords(encoded);
  return {
    coordinates,
    decodeMs: nowMs() - t0,
    vertexCount: coordinates.length,
  };
}
